// Copyright (C) 2021 Bystronic Laser AG

import {
  ChartGenerator,
  ProcedureName,
} from '@/models/Charts/abstract/chartGenerator';
import { FilterTimeSpanEnum } from '@/models/enums/FilterTimeSpanEnum';
import i18n from '@/i18n';
import { EChartsOption, SeriesOption } from 'echarts';
import { GeneratorParams } from './generatorParams';
import { SalesTopBySalesData } from './chartsData';
import { FilterTimeAxisSpanEnum } from '../enums/FilterTimeAxisSpanEnum';
import { mockTopSalespeopleData } from './mockWidgetSelectorData';
import { YAXisOption } from 'echarts/types/dist/shared';
import { DEFAULT_TOP_NUMBER } from './salesTopBySalesGenerator';
import { metricsService } from '@/services/metrics.service';

export class AmountVsNumberOfQuotesBySalespeopleGenerator extends ChartGenerator<
  SalesTopBySalesData[]
> {
  constructor(procedure: ProcedureName, public tenantIdDh: number) {
    super(procedure);
  }

  override getData(
    selectedDevices: string[],
    selectedShifts: number[],
    timeSpan: FilterTimeSpanEnum | [string, string],
    timeAxisSpan?: FilterTimeAxisSpanEnum,
    params?: { [key: string]: any },
  ) {
    const startDate = (timeSpan as [string, string])?.[0];
    const endDate = (timeSpan as [string, string])?.[1];
    return metricsService.getCachedTopSalespeopleData(
      this.tenantIdDh,
      params?.topNumber || DEFAULT_TOP_NUMBER,
      startDate,
      endDate,
      this.controller,
    );
  }

  override updateOptions(
    data: SalesTopBySalesData[],
    parameters: GeneratorParams = {},
    prevOptions?: EChartsOption,
  ): EChartsOption {
    return {
      legend: {
        show: true,
        type: 'scroll',
        bottom: 0,
      },
      tooltip: {
        confine: true,
      },
      xAxis: {
        type: 'value' as const,
        axisLabel: {
          margin: 4,
          fontSize: parameters.clientWidth! < 357 ? 10 : 12,
        },
      },
      yAxis: {
        type: 'category' as const,
        inverse: true,
        data: this.getSalespeopleNames(data),
        axisLabel: {
          overflow: 'truncate',
          width: 130,
        },
      } as YAXisOption, // overflow=truncate not recognized
      grid: {
        top: 16,
        bottom: 40,
        left: 8,
        containLabel: true,
      },
      series: this.generateSeries(data),
    };
  }

  private getSalespeopleNames(data: SalesTopBySalesData[]): string[] {
    return data.map((dataItem) => dataItem.Salesperson);
  }

  private generateSeries(data: SalesTopBySalesData[]): SeriesOption[] {
    return ['amount_conversion', 'quotes_conversion'].map((field) => ({
      type: 'bar' as const,
      name: i18n.t(`report.business_console.${field}`).toString(),
      data: this.generateSeriesData(data, field as keyof SalesTopBySalesData),
    }));
  }

  private generateSeriesData(
    data: SalesTopBySalesData[],
    field: keyof SalesTopBySalesData,
  ): number[] {
    return data.map(
      (dataItem) => dataItem[field] as number, // we only get numeric fields
    );
  }

  override getMockData(): SalesTopBySalesData[] {
    return mockTopSalespeopleData();
  }
}
